import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Bell,
  Radio,
  CheckCircle,
  Star,
  Users,
  Gift,
  Zap,
  Heart,
  Rss,
  ExternalLink,
  Music,
  Headphones,
  Video,
} from 'lucide-react';
import QoreTalksShell from './QoreTalksShell';
import { subscribeToQoreTalks } from '../../services/qoreTalksApi';
import { PLATFORM_LINKS, getRssFeedUrl } from './qoreTalksUtils';

const Subscribe = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [notifications, setNotifications] = useState({
    launch: true,
    episodes: true,
    exclusive: true,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await subscribeToQoreTalks({ email, preferences: notifications });
      setIsSubmitted(true);
      setEmail('');
    } catch (err) {
      alert(err.message || 'Could not subscribe');
    } finally {
      setSubmitting(false);
    }
  };

  const SubscribeForm = ({ id }) =>
    isSubmitted ? (
      <div className="qt-success" id={id}>
        <CheckCircle size={28} color="#34d399" />
        <p style={{ margin: 0 }}>{t('qoreTalks.subscribeSuccess')}</p>
      </div>
    ) : (
      <form id={id} onSubmit={handleSubmit} className="qt-form" style={{ maxWidth: 480, margin: '0 auto' }}>
        <input
          type="email"
          className="qt-input"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-label="Email"
        />
        <button type="submit" className="qt-btn qt-btn--primary qt-btn--lg" disabled={submitting} style={{ width: '100%' }}>
          <Mail size={18} />
          {submitting ? '…' : t('nav.subscribe')}
        </button>
      </form>
    );

  const PLATFORM_ICONS = { spotify: Music, apple: Headphones, youtube: Video, google: Radio, rss: Rss };

  const benefits = [
    { icon: Bell, titleKey: 'subscribeBenefit1', descKey: 'subscribeBenefit1Desc' },
    { icon: Star, titleKey: 'subscribeBenefit2', descKey: 'subscribeBenefit2Desc' },
    { icon: Users, titleKey: 'subscribeBenefit3', descKey: 'subscribeBenefit3Desc' },
    { icon: Gift, titleKey: 'subscribeBenefit4', descKey: 'subscribeBenefit4Desc' },
  ];

  return (
    <QoreTalksShell
      activeTab="subscribe"
      hero={{
        badge: t('qoreTalks.subscribeHeroBadge'),
        title: t('qoreTalks.subscribeHeroTitle'),
        titleAccent: t('qoreTalks.subscribeHeroAccent'),
        subtitle: t('qoreTalks.subscribeHeroDesc'),
        extra: <SubscribeForm id="qt-subscribe-main" />,
      }}
      mainClass="qt-main--wide"
    >
      <section className="qt-section">
        <h2 className="qt-section__title">{t('qoreTalks.whySubscribe')}</h2>
        <p className="qt-section__subtitle">{t('qoreTalks.whySubscribeDesc')}</p>
        <div className="qt-benefit-grid">
          {benefits.map(({ icon: Icon, titleKey, descKey }) => (
            <div className="qt-benefit" key={titleKey}>
              <div className="qt-benefit__icon">
                <Icon size={22} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem' }}>{t(`qoreTalks.${titleKey}`)}</h3>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem', lineHeight: 1.55 }}>
                {t(`qoreTalks.${descKey}`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="qt-section qt-section--alt">
        <h2 className="qt-section__title">{t('qoreTalks.listenEverywhere')}</h2>
        <p className="qt-section__subtitle">{t('qoreTalks.listenEverywhereDesc')}</p>
        <div className="qt-platform-grid">
          {PLATFORM_LINKS.filter((p) => p.key !== 'rss').map((p) => {
            const Icon = PLATFORM_ICONS[p.key] || Radio;
            const hasUrl = Boolean(p.url);
            const inner = (
              <>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${p.color}22`,
                    color: p.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 0.75rem',
                  }}
                >
                  <Icon size={26} />
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{p.name}</span>
                {!hasUrl && <span className="qt-platform__soon">{t('qoreTalks.platformComingSoon')}</span>}
              </>
            );
            return hasUrl ? (
              <a
                key={p.key}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="qt-platform"
              >
                {inner}
                <ExternalLink size={14} style={{ marginTop: 6, opacity: 0.5 }} />
              </a>
            ) : (
              <div key={p.key} className="qt-platform qt-platform--disabled">
                {inner}
              </div>
            );
          })}
        </div>
        <p className="qt-rss-banner">
          <Rss size={18} aria-hidden />
          {t('qoreTalks.rssNote')} —{' '}
          <a href={getRssFeedUrl()} className="qt-rss-link">
            {t('qoreTalks.rssFeed')}
          </a>
        </p>
      </section>

      <section className="qt-section">
        <h2 className="qt-section__title">{t('qoreTalks.prefsTitle')}</h2>
        <p className="qt-section__subtitle">{t('qoreTalks.prefsDesc')}</p>
        <div className="qt-pref-list" style={{ maxWidth: 560, margin: '0 auto 1.5rem' }}>
          {[
            { key: 'launch', title: 'prefLaunch', desc: 'prefLaunchDesc' },
            { key: 'episodes', title: 'prefEpisodes', desc: 'prefEpisodesDesc' },
            { key: 'exclusive', title: 'prefExclusive', desc: 'prefExclusiveDesc' },
          ].map((item) => (
            <label
              key={item.key}
              className={`qt-pref${notifications[item.key] ? ' qt-pref--on' : ''}`}
            >
              <input
                type="checkbox"
                checked={notifications[item.key]}
                onChange={() =>
                  setNotifications((prev) => ({ ...prev, [item.key]: !prev[item.key] }))
                }
              />
              <div>
                <strong>{t(`qoreTalks.${item.title}`)}</strong>
                <span>{t(`qoreTalks.${item.desc}`)}</span>
              </div>
            </label>
          ))}
        </div>
        {!isSubmitted && (
          <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
            {t('qoreTalks.useHeroForm')}
          </p>
        )}
      </section>

      <section className="qt-section qt-section--alt">
        <div className="qt-cta-panel">
          <Zap size={28} color="#a5b4fc" />
          <Heart size={28} color="#f43f5e" style={{ margin: '0 0.25rem' }} />
          <Star size={28} color="#fbbf24" />
          <h3>{t('qoreTalks.finalCtaTitle')}</h3>
          <p>{t('qoreTalks.finalCtaDesc')}</p>
          <Link to="/qoretalks" className="qt-btn qt-btn--ghost">
            {t('qoreTalks.browseEpisodes')}
          </Link>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#64748b' }}>
            {t('qoreTalks.unsubscribeNote')}
          </p>
        </div>
      </section>
    </QoreTalksShell>
  );
};

export default Subscribe;